import React, { useContext, useEffect, useState, ReactNode } from 'react'
import _ from 'lodash'
import SelectAllContent from '../shared/SelectAllContent'
import { useRcmTableData } from '../../hooks/useRcmTableData'

// Types
interface MultiSelectState {
  currentlyDisplayedCheckboxes: string[]
  selectAllOnThisPage: boolean
  selectedWholeResult: boolean
  totalItemsInSection: number
  selectedIds: string[]
  loading: boolean
  filter: any
  notToShowBanner: boolean
}

interface TableMultiSelectionContextValue {
  multiSelectState: MultiSelectState
  setMultiSelectState: React.Dispatch<React.SetStateAction<MultiSelectState>>
  showSelectAllContent: (forceRefresh?: boolean) => void
  deselectAll: () => void
}

interface TableMultiSelectionWrapperProps {
  assembleFilterFunction?: (filter: any, flag: boolean) => any
  getTotalNumberFunction?: (filter?: any) => Promise<number>
  texts?: {
    allItemsSelectedText?: string
    selectAllItemsText?: string
  }
  textParams?: Record<string, any>
  children: ReactNode
  onChange?: (selectedIds: string[]) => void
  isSelectTotalEnabled?: boolean
  preselectedIds?: string[]
  isWorkflow?: boolean
  isWorkflowCancelled?: boolean
  isPdrFlow?: boolean
  onSelectAll?: () => void
  onClearAll?: () => void
}

interface SelectAllRowsProps {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  allSelected?: boolean
  isDisabled?: boolean
  reset?: boolean
}

interface SelectRowProps {
  row: { id: string }
  disabled?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  notToShowBannerForComponent?: boolean
  allSelected?: boolean
}

export const TableMultiSelectionContext = React.createContext<TableMultiSelectionContextValue | null>(null)
export const TableMultiSelectionConsumer = TableMultiSelectionContext.Consumer

export function TableMultiSelectionWrapper({
  assembleFilterFunction,
  getTotalNumberFunction,
  texts,
  textParams,
  children,
  onChange,
  isSelectTotalEnabled = true,
  preselectedIds,
  isWorkflow,
  isWorkflowCancelled,
  isPdrFlow,
  onSelectAll,
  onClearAll
}: TableMultiSelectionWrapperProps) {
  const tableData = useRcmTableData()
  const [multiSelectState, setMultiSelectState] = useState<MultiSelectState>({
    currentlyDisplayedCheckboxes: [],
    selectAllOnThisPage: false,
    selectedWholeResult: false,
    totalItemsInSection: 0,
    selectedIds: preselectedIds ? _.clone(preselectedIds) : [],
    loading: false,
    filter: null,
    notToShowBanner: true
  })

  // Deselect all when page changes, switching between grid/list view, or when entries change
  useEffect(() => {
    if (!preselectedIds || preselectedIds.length === 0) {
      deselectAll()
    }
  }, [tableData.entries, tableData.isGridView, tableData.page])

  useEffect(() => {
    if (preselectedIds) {
      setMultiSelectState((prevState) => {
        return {
          ...prevState,
          selectedIds: preselectedIds
        }
      })
    }
  }, [preselectedIds])

  useEffect(() => {
    if (onChange) {
      onChange(multiSelectState.selectedIds)
    }
  }, [multiSelectState.selectedIds])

  const deselectAll = () => {
    setMultiSelectState((prevState) => {
      return {
        ...prevState,
        selectAllOnThisPage: false,
        selectedWholeResult: false,
        selectedIds: [],
        notToShowBanner: true,
        filter: null, // Clear cached filter to force refresh of count next time
        totalItemsInSection: 0 // Reset count
      }
    })
  }

  const showSelectAllContent = (forceRefresh = false) => {
    if (!multiSelectState.currentlyDisplayedCheckboxes.length) {
      setMultiSelectState((prevState) => {
        return {
          ...prevState,
          loading: false
        }
      })

      return
    }

    if (assembleFilterFunction) {
      const assembledFilter = {
        ...assembleFilterFunction({ ...tableData.filter }, true)
      }

      if (!forceRefresh && _.isEqual(multiSelectState.filter, assembledFilter)) {
        setMultiSelectState((prevState) => {
          return {
            ...prevState,
            loading: false
          }
        })
        return
      }

      if (getTotalNumberFunction) {
        getTotalNumberFunction({ ...assembledFilter }).then((data) => {
          setMultiSelectState((prevState) => {
            return {
              ...prevState,
              loading: false,
              totalItemsInSection: data,
              filter: assembledFilter
            }
          })
        })
      }
    } else {
      if (isWorkflow && getTotalNumberFunction) {
        getTotalNumberFunction().then((data) => {
          setMultiSelectState((prevState) => {
            return {
              ...prevState,
              loading: false,
              totalItemsInSection: data,
              filter: {}
            }
          })
        })
      }
    }
    if (isPdrFlow && getTotalNumberFunction) {
      getTotalNumberFunction().then((data) => {
        setMultiSelectState((prevState) => {
          return {
            ...prevState,
            loading: false,
            totalItemsInSection: data
          }
        })
      })
    }
  }

  const multiSelectionApi: TableMultiSelectionContextValue = {
    multiSelectState,
    setMultiSelectState,
    showSelectAllContent,
    deselectAll
  }

  return (
    <>
      <TableMultiSelectionContext.Provider value={multiSelectionApi}>
        {isSelectTotalEnabled &&
          multiSelectState.selectAllOnThisPage &&
          !multiSelectState.loading &&
          multiSelectState.selectedIds.length > 0 &&
          multiSelectState.notToShowBanner && (
            <SelectAllContent
              {...texts}
              isWorkflowCancelled={isWorkflowCancelled}
              textParams={{
                allItems: multiSelectState.totalItemsInSection,
                number: multiSelectState.selectedIds.length,
                ...textParams
              }}
              selectedItems={multiSelectState.selectedIds}
              total={multiSelectState.totalItemsInSection}
              onSelectAllClick={() => {
                setMultiSelectState((prevState) => {
                  return {
                    ...prevState,
                    selectedWholeResult: true
                  }
                })
                if (onSelectAll) {
                  onSelectAll()
                }
              }}
              onClearSelectionClick={() => {
                setMultiSelectState((prevState) => {
                  return {
                    ...prevState,
                    selectAllOnThisPage: false,
                    selectedWholeResult: false,
                    selectedIds: [],
                    notToShowBanner: true
                  }
                })
                if (onClearAll) {
                  onClearAll()
                }
              }}
            />
          )}
        {children}
      </TableMultiSelectionContext.Provider>
    </>
  )
}

export function SelectAllRows({ onChange, allSelected, isDisabled, reset }: SelectAllRowsProps) {
  const tableMultiSelectionContext = useContext(TableMultiSelectionContext)

  if (!tableMultiSelectionContext) {
    throw new Error('SelectAllRows must be used within TableMultiSelectionWrapper')
  }

  const { multiSelectState } = tableMultiSelectionContext

  const difference = _.difference(multiSelectState.currentlyDisplayedCheckboxes, multiSelectState.selectedIds)

  const preselect =
    (multiSelectState.selectAllOnThisPage ||
      (multiSelectState.selectedIds.length > 0 &&
        multiSelectState.currentlyDisplayedCheckboxes.length === multiSelectState.selectedIds.length &&
        difference.length === 0)) &&
    !isDisabled

  return (
    <input
      type="checkbox"
      name="selectAll"
      onChange={
        onChange
          ? onChange
          : (e) => {
              const { checked } = e.target
              tableMultiSelectionContext.setMultiSelectState((prevState) => {
                return {
                  ...prevState,
                  loading: checked,
                  selectAllOnThisPage: checked,
                  selectedWholeResult: false, // Don't select all items, only current page
                  selectedIds: checked ? prevState.currentlyDisplayedCheckboxes : []
                }
              })
              checked && tableMultiSelectionContext.showSelectAllContent()
            }
      }
      checked={(preselect || allSelected) && !reset}
      disabled={!!isDisabled}
    />
  )
}

export function SelectRow({ row, disabled, onChange, notToShowBannerForComponent, allSelected }: SelectRowProps) {
  const tableMultiSelectionContext = useContext(TableMultiSelectionContext)

  if (!tableMultiSelectionContext) {
    throw new Error('SelectRow must be used within TableMultiSelectionWrapper')
  }

  useEffect(() => {
    if (disabled) {
      return
    }
    tableMultiSelectionContext.setMultiSelectState((prevState) => {
      const newCurrentlyDisplayedCheckboxes = _.union(prevState.currentlyDisplayedCheckboxes, [row?.id])
      return {
        ...prevState,
        currentlyDisplayedCheckboxes: newCurrentlyDisplayedCheckboxes
      }
    })
    return () => {
      tableMultiSelectionContext.setMultiSelectState((prevState) => {
        const newCurrentlyDisplayedCheckboxes = prevState.currentlyDisplayedCheckboxes.filter((val) => val !== row?.id)
        return {
          ...prevState,
          currentlyDisplayedCheckboxes: newCurrentlyDisplayedCheckboxes
        }
      })
    }
  }, [row?.id])

  return (
    <input
      type="checkbox"
      disabled={disabled}
      name="selectAll"
      onChange={
        onChange
          ? onChange
          : (e) => {
              const { checked } = e.target
              tableMultiSelectionContext.setMultiSelectState((prevState) => {
                const newSelectedRowsState = checked
                  ? _.union(prevState.selectedIds, [row.id])
                  : prevState.selectedIds.filter((val) => val !== row.id)
                const checkedAllOnThisPage =
                  checked && prevState.currentlyDisplayedCheckboxes.every((v) => newSelectedRowsState.includes(v))
                checkedAllOnThisPage && tableMultiSelectionContext.showSelectAllContent()

                return {
                  ...prevState,
                  selectedWholeResult: false,
                  selectAllOnThisPage: checkedAllOnThisPage,
                  selectedIds: newSelectedRowsState,
                  notToShowBanner: notToShowBannerForComponent ? false : true
                }
              })
            }
      }
      checked={tableMultiSelectionContext.multiSelectState.selectedIds.includes(row.id) || !!allSelected}
    />
  )
}
